<template>
  <div class="settings-targets-widget">
    <div class="targets-header">
      <h3 class="targets-title">Settings Scope</h3>
      <p class="targets-description">
        Choose whether to modify User or Workspace settings
      </p>
    </div>
    
    <div class="targets-tabs" role="tablist">
      <button
        v-for="target in targets"
        :key="target.value"
        :class="[
          'target-tab',
          { 'target-tab--active': modelValue === target.value }
        ]"
        :aria-selected="modelValue === target.value"
        :aria-controls="`${target.value}-panel`"
        role="tab"
        @click="selectTarget(target.value)"
      >
        <Icon :name="target.icon" :size="16" />
        <span class="target-label">{{ target.label }}</span>
        <span v-if="target.description" class="target-subtitle">
          {{ target.description }}
        </span>
      </button>
    </div>
    
    <div class="targets-content">
      <div
        v-for="target in targets"
        :key="`${target.value}-panel`"
        v-show="modelValue === target.value"
        :id="`${target.value}-panel`"
        class="target-panel"
        role="tabpanel"
        :aria-labelledby="`${target.value}-tab`"
      >
        <div class="panel-info">
          <Icon :name="target.icon" :size="20" />
          <div class="panel-text">
            <h4 class="panel-title">{{ target.label }} Settings</h4>
            <p class="panel-description">{{ target.fullDescription }}</p>
          </div>
        </div>
        
        <div v-if="target.actions?.length" class="panel-actions">
          <Button
            v-for="action in target.actions"
            :key="action.label"
            :variant="action.variant || 'secondary'"
            :size="action.size || 'small'"
            :icon="action.icon"
            @click="$emit('action', action.key, target.value)"
          >
            {{ action.label }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Button, Icon } from '@/components/atoms';

export interface TargetAction {
  key: string;
  label: string;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'icon';
  size?: 'small' | 'medium' | 'large';
}

export interface SettingsTarget {
  value: 'user' | 'workspace';
  label: string;
  description: string;
  fullDescription: string;
  icon: string;
  actions?: TargetAction[];
}

export interface SettingsTargetsWidgetProps {
  modelValue: 'user' | 'workspace';
  showActions?: boolean;
}

const props = withDefaults(defineProps<SettingsTargetsWidgetProps>(), {
  showActions: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: 'user' | 'workspace'];
  'action': [actionKey: string, scope: 'user' | 'workspace'];
}>();

const targets = computed((): SettingsTarget[] => [
  {
    value: 'user',
    label: 'User',
    description: 'Global settings',
    fullDescription: 'Settings that apply to all workspaces and projects on this machine.',
    icon: 'account',
    actions: props.showActions ? [
      { key: 'export', label: 'Export', icon: 'export' },
      { key: 'import', label: 'Import', icon: 'import' },
      { key: 'reset', label: 'Reset', icon: 'refresh' },
    ] : undefined,
  },
  {
    value: 'workspace',
    label: 'Workspace',
    description: 'Project settings',
    fullDescription: 'Settings that only apply to the current workspace or project.',
    icon: 'folder-opened',
    actions: props.showActions ? [
      { key: 'export', label: 'Export', icon: 'export' },
      { key: 'import', label: 'Import', icon: 'import' },
      { key: 'reset', label: 'Reset', icon: 'refresh' },
    ] : undefined,
  },
]);

const selectTarget = (target: 'user' | 'workspace') => {
  emit('update:modelValue', target);
};
</script>

<style scoped lang="scss">
.settings-targets-widget {
  background-color: var(--vscode-backgroundSecondary);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.targets-header {
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--vscode-border);
}

.targets-title {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--vscode-foreground);
}

.targets-description {
  margin: 0;
  font-size: 12px;
  color: var(--vscode-foreground);
  opacity: 0.8;
}

.targets-tabs {
  display: flex;
  background-color: var(--vscode-background);
}

.target-tab {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-lg);
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: var(--vscode-font-family);
  color: var(--vscode-foreground);
  opacity: 0.7;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
    background-color: var(--vscode-list-hoverBackground);
  }
  
  &--active {
    opacity: 1;
    border-bottom-color: var(--vscode-focusBorder);
    background-color: var(--vscode-backgroundSecondary);
  }
  
  &:focus-visible {
    outline: 1px solid var(--vscode-focusBorder);
    outline-offset: -1px;
  }
}

.target-label {
  font-size: var(--vscode-font-size);
  font-weight: 500;
}

.target-subtitle {
  font-size: 11px;
  opacity: 0.8;
}

.targets-content {
  padding: var(--spacing-lg);
}

.target-panel {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.panel-info {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
}

.panel-text {
  flex: 1;
}

.panel-title {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--vscode-foreground);
}

.panel-description {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--vscode-foreground);
  opacity: 0.8;
}

.panel-actions {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

// Responsive adjustments
@media (max-width: 768px) {
  .targets-tabs {
    flex-direction: column;
  }
  
  .target-tab {
    flex-direction: row;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-lg);
    border-bottom: none;
    border-right: 2px solid transparent;
    
    &--active {
      border-bottom: none;
      border-right-color: var(--vscode-focusBorder);
    }
  }
  
  .target-subtitle {
    margin-left: auto;
  }
  
  .panel-info {
    flex-direction: column;
    text-align: center;
  }
  
  .panel-actions {
    justify-content: center;
  }
}
</style>
